import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import type { Vacancy, VacancyWithAtsJob, VacancyWithIndeedJob } from 'clients/AutoCompleteClient/types';
import type { JobFormValues } from 'shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/components/EditEnrichedVacancyForm/EditEnrichedVacancy.types';

import { reachoutClient } from 'clients/ReachoutClient/ReachoutClient';
import { logger } from 'config/logger';
import { mapJobImprovedGetDetailOutputToJobFormValues } from 'shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/utils/EnrichVacancyModal.mappers';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCreateSnackbar } from 'shared/legacy/contexts/SnackbarContext/useCreateSnackbar';
import { QueryKey } from 'types/query';

type UnifiedProcessVacancyInput = Vacancy | { text: string } | { file: File };

export const useProcessVacancyMutation = () => {
  const { dictionary } = useContext(LocalizationContext);
  const queryClient = useQueryClient();
  const createSnackbar = useCreateSnackbar();

  return useMutation<JobFormValues, Error, UnifiedProcessVacancyInput>(
    async (payload: UnifiedProcessVacancyInput) => {
      if (isImprovedVacancy(payload)) throw Error(dictionary.youCannotImproveAlreadyImprovedVacancy);

      const cachedData = queryClient.getQueryData<JobFormValues>(getCacheKey(payload));
      if (cachedData) return cachedData;

      if (isFile(payload)) {
        const { data } = await reachoutClient.processVacancy({
          input_type: 'file',
          vacancy_file: payload.file,
        });

        return mapJobImprovedGetDetailOutputToJobFormValues(data);
      }

      if (isText(payload) || isIndeedVacancy(payload)) {
        const { data } = await reachoutClient.processVacancy({
          input_type: 'text',
          input_text: isText(payload) ? payload.text : payload.originalIndeedJob?.description || '',
        });

        return mapJobImprovedGetDetailOutputToJobFormValues(data);
      }

      if (isAtsVacancy(payload)) {
        const { data } = await reachoutClient.processAtsVacancy(payload.job_ats_id);

        return mapJobImprovedGetDetailOutputToJobFormValues(data);
      }

      throw Error('Payload is invalid');
    },
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(getCacheKey(variables), data);
      },
      onError: (error) => {
        logger.error(error);

        createSnackbar(error instanceof Error ? error.message : dictionary.somethingWentWrong, {
          variant: 'danger',
          duration: 10000,
          title: dictionary.errorProcessingVacancy,
        });
      },
    },
  );
};

const getCacheKey = (payload: UnifiedProcessVacancyInput) => {
  if (isFile(payload)) {
    const file = payload.file;
    return [QueryKey.useProcessVacancy, 'file', file.name, file.size, file.lastModified];
  }

  if (isText(payload) || isIndeedVacancy(payload)) {
    const text = isText(payload) ? payload.text : payload.originalIndeedJob?.description || '';
    return [QueryKey.useProcessVacancy, 'text', hashString(text)];
  }

  if (isAtsVacancy(payload)) {
    return [QueryKey.useProcessAtsVacancy, payload.job_ats_id];
  }

  throw Error('Payload is invalid');
};

// Small, deterministic 32-bit hash to keep query keys compact
const hashString = (value: string): string => {
  let hash = 5381; // djb2
  for (let i = 0; i < value.length; i += 1) {
    // hash * 33 ^ char
    hash = (hash * 33) ^ value.charCodeAt(i);
    // Force to 32-bit
    hash >>>= 0;
  }
  // Return unsigned 32-bit hex string for readability
  return `0x${hash.toString(16)}`;
};

const isFile = (payload: Record<string, unknown>): payload is { file: File } => {
  return 'file' in payload;
};

const isText = (payload: Record<string, unknown>): payload is { text: string } => {
  return 'text' in payload;
};

const isAtsVacancy = (payload: Record<string, unknown>): payload is VacancyWithAtsJob => {
  return 'job_ats_id' in payload && !!payload.job_ats_id;
};

const isIndeedVacancy = (payload: Record<string, unknown>): payload is VacancyWithIndeedJob => {
  return 'job_indeed_id' in payload && !!payload.job_indeed_id;
};

const isImprovedVacancy = (payload: Record<string, unknown>): boolean => {
  return 'is_improved' in payload && !!payload.is_improved;
};
